// Phase T2.0A Prompt 2 — Admin flyer upload / replace / remove.
//
// Reusable across Tournament + Round Robin admin pages. Client-side guards
// on MIME + size (5 MB) mirror the backend and Supabase Storage bucket
// limits so the user gets an immediate rejection instead of a round-trip.

import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, Trash, ImageSquare } from '@phosphor-icons/react';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export default function EventFlyerUpload({
  flyerUrl,           // current flyer URL (or null)
  onUpload,           // async (file) => Promise<updatedEvent>
  onRemove,           // async ()     => Promise<updatedEvent>
  eventName,
  disabled = false,
}) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const validate = (file) => {
    if (!file) return 'No file selected.';
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Please choose a JPG, PNG, or WEBP image.';
    }
    if (file.size > MAX_BYTES) {
      return 'That image is over 5 MB. Please choose a smaller file.';
    }
    return null;
  };

  const handleFile = async (file) => {
    const err = validate(file);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await onUpload(file);
      toast.success(flyerUrl ? 'Flyer replaced.' : 'Flyer uploaded.');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Upload failed.';
      toast.error(typeof detail === 'string' ? detail : 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the event flyer?')) return;
    setBusy(true);
    try {
      await onRemove();
      toast.success('Flyer removed.');
    } catch {
      toast.error('Could not remove flyer.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="event-flyer-upload" className="space-y-3">
      {flyerUrl ? (
        <div className="relative border border-[#e3dcd1] rounded-xl overflow-hidden bg-[#f7f4ed]" data-testid="event-flyer-preview">
          <img
            src={flyerUrl}
            alt={eventName ? `${eventName} poster` : 'Event flyer'}
            className="block w-full h-auto max-h-[320px] object-contain mx-auto"
          />
        </div>
      ) : (
        <div
          data-testid="event-flyer-empty"
          className="border border-dashed border-[#e3dcd1] rounded-xl bg-[#faf7f0] px-4 py-6 flex flex-col items-center justify-center text-center"
        >
          <ImageSquare size={28} weight="duotone" className="text-[#9aa39d] mb-1" />
          <div className="text-[13px] text-[#013523] font-medium">No flyer yet</div>
          <div className="text-[11.5px] text-[#6a766e] mt-0.5">JPG, PNG or WEBP · up to 5 MB</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          data-testid="event-flyer-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          data-testid="event-flyer-upload-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#013523] text-white text-xs min-h-[44px] disabled:opacity-50"
        >
          <UploadSimple size={14} />
          {busy ? 'Uploading…' : (flyerUrl ? 'Replace flyer' : 'Upload flyer')}
        </button>
        {flyerUrl && (
          <button
            type="button"
            disabled={disabled || busy}
            onClick={handleRemove}
            data-testid="event-flyer-remove-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#e3dcd1] text-[#8a1c1c] text-xs min-h-[44px] disabled:opacity-50"
          >
            <Trash size={14} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
